import React, { useState, type RefObject } from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import TextButton from '~/components/atom/TextButton';
import { Heading5, Label, Variant } from '~/components/atom/Typography';
import ResponsivePopup from '~/components/molecule/ResponsivePopup';
import type {
  LfaServiceDetailsFieldsFragment,
  LfaServiceLocationFieldsFragment,
} from '~/graphql/types';
import type { UpdateSettingsAction } from '../../../../reducers/settingsReducer';
import Textarea from '~/components/molecule/Textarea';
import Button from '~/components/atom/Button';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import withIconTooltip from '~/hocs/withIconTooltip';

const text = {
  locationSettings: 'Locatie instellingen',
  noteForTheAgent: 'Notitie voor de agent',
  preferredTooltip: 'De voorkeur wordt getoond aan de call-agent',
  addANoteThatWillBeVisibleToTheCallCenterAgent:
    'Voeg een notitie toe die zichtbaar is voor de call center agent...',
  cancel: 'Annuleren',
  save: 'Opslaan',
  preferred: 'Voorkeurslocatie',
};

export type Props = {
  dataTestId?: string;
  className?: string;
  openerRef: RefObject<HTMLElement>;
  option: LfaServiceDetailsFieldsFragment;
  location: LfaServiceLocationFieldsFragment;
  currentLocation?: LfaServiceLocationFieldsFragment;
  dispatch: React.Dispatch<UpdateSettingsAction>;
  onClose: () => void;
};

const LocationSettingsPopup: React.FCC<Props> = ({
  dataTestId,
  className,
  openerRef,
  option,
  location,
  currentLocation,
  dispatch,
  onClose,
}) => {
  const [note, setNote] = useState(currentLocation?.note || '');
  const [weight, setWeight] = useState(currentLocation?.weight || 0);

  return (
    <ResponsivePopup
      isVisible
      openerRef={openerRef}
      rootId="location-settings"
      onClickOutside={onClose}
    >
      <JustificationContainer
        backgroundColor={{ group: 'white' }}
        padding={['m']}
        direction="column"
        gap="m"
        dataTestId={dataTestId}
        className={className}
        boxShadow={'base'}
        border={{ radius: 'm' }}
      >
        <Heading5 margin={[null]}>{text.locationSettings}</Heading5>

        <JustificationContainer direction="column" gap="s" width="100%">
          <ToggleCheckbox
            label={
              <LabelWithIcon
                tooltipMessage={text.preferredTooltip}
                width="100%"
                margin={[null]}
              >
                {text.preferred}
              </LabelWithIcon>
            }
            size="small"
            value={weight > 0}
            onChange={() => setWeight(weight > 0 ? 0 : 10)}
          />
          <JustificationContainer direction="column" gap="xxxs">
            <JustificationContainer
              align="center"
              justification="space-between"
              width="100%"
            >
              <Label margin={[null]} size="s" variant={Variant.primary}>
                {text.noteForTheAgent}
              </Label>
              <TextButton
                icon="delete"
                appearance="danger"
                padding={[null]}
                onClick={() => setNote('')}
              />
            </JustificationContainer>
            <Textarea
              name="location-note"
              value={note}
              onChange={value => setNote(value || '')}
              placeholder={text.addANoteThatWillBeVisibleToTheCallCenterAgent}
            />
          </JustificationContainer>
        </JustificationContainer>

        <JustificationContainer
          width="100%"
          justification="end"
          gap="s"
          align="center"
        >
          <TextButton
            label={text.cancel}
            onClick={onClose}
            appearance="danger"
          />
          <Button
            appearance="secondary"
            label={text.save}
            onClick={() => {
              dispatch({
                type: 'SET_LOCATION_NOTE_AND_WEIGHT',
                payload: {
                  serviceId: option.id,
                  locationType: location.type,
                  note,
                  weight,
                },
              });
              onClose();
            }}
          />
        </JustificationContainer>
      </JustificationContainer>
    </ResponsivePopup>
  );
};

const LabelWithIcon = withIconTooltip(Label);

export default LocationSettingsPopup;
